import { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatutoryExport } from "@/components/StatutoryExport";
import { Plus, X, Pencil, Trash2, Database } from "lucide-react";
import { toast } from "sonner";

export const FORM_TAB_LABELS = {
  "I": { en: "Form I · Cash Book", hi: "प्रपत्र I · रोकड़ बही" },
  "II": { en: "Form II · Proceeding Book", hi: "प्रपत्र II · कार्यवाही पुस्तक" },
  "III": { en: "Form III · Inspection Book", hi: "प्रपत्र III · निरीक्षण पुस्तक" },
  "IV": { en: "Form IV · Receipt Book", hi: "प्रपत्र IV · रसीद बही" },
  "V": { en: "Form V · Attendance", hi: "प्रपत्र V · उपस्थिति" },
  "VI": { en: "Form VI · Demand & Collection", hi: "प्रपत्र VI · मांग एवं वसूली" },
  "VII": { en: "Form VII · Court Cases", hi: "प्रपत्र VII · न्यायालय वाद" },
  "VIII": { en: "Form VIII · Land Acquisition", hi: "प्रपत्र VIII · भूमि अधिग्रहण" },
  "IX": { en: "Form IX · Fees & Fines", hi: "प्रपत्र IX · शुल्क एवं जुर्माना" },
  "X": { en: "Form X · Inventory", hi: "प्रपत्र X · इन्वेंटरी" },
  "XI": { en: "Form XI · Works", hi: "प्रपत्र XI · निर्माण कार्य" },
  "XII": { en: "Form XII · Muster Roll", hi: "प्रपत्र XII · मस्टर रोल" },
  "XIII-A": { en: "Form XIII-A · Roads", hi: "प्रपत्र XIII-A · सड़कें" },
  "XIII-B": { en: "Form XIII-B · Land", hi: "प्रपत्र XIII-B · भूमि" },
  "XIII-C": { en: "Form XIII-C · Other Immovable", hi: "प्रपत्र XIII-C · अन्य अचल" },
  "XIV": { en: "Form XIV · Movable Property", hi: "प्रपत्र XIV · चल संपत्ति" },
  "XV": { en: "Form XV · Library Stock", hi: "प्रपत्र XV · पुस्तकालय स्टॉक" },
  "XVI": { en: "Form XVI · Library Issue", hi: "प्रपत्र XVI · पुस्तक निर्गम" },
  "XVII": { en: "Form XVII · Security Deposits", hi: "प्रपत्र XVII · प्रतिभूति जमा" },
  "XVIII": { en: "Form XVIII · Receipt Books", hi: "प्रपत्र XVIII · रसीद बही रजिस्टर" },
  "XIX": { en: "Form XIX · Despatch", hi: "प्रपत्र XIX · प्रेषण" },
  "XX": { en: "Form XX · Inward", hi: "प्रपत्र XX · आवक" },
};

export const formTabLabel = (code, lang) =>
  FORM_TAB_LABELS[code]?.[lang === "hi" ? "hi" : "en"] || `Form ${code}`;

export const StatutoryTabs = ({ moduleLabel, codes, active, onChange }) => {
  const { lang } = useLang();
  const cls = (on) =>
    `px-5 py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${on ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"}`;
  return (
    <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
      {moduleLabel && (
        <button onClick={() => onChange("module")} data-testid="reg-tab-module" className={cls(active === "module")}>
          {moduleLabel}
        </button>
      )}
      {codes.map((c) => (
        <button key={c} onClick={() => onChange(c)} data-testid={`reg-tab-${c}`} className={cls(active === c)}>
          {formTabLabel(c, lang)}
        </button>
      ))}
    </div>
  );
};

export const StatutoryRegister = ({ code }) => {
  const { lang } = useLang();
  const { user } = useAuth();
  const lab = (obj) => (obj && (obj[lang] || obj.en)) || "";
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/statutory-forms").then((r) => setForm(r.data.find((f) => f.code === code) || null));
  }, [code]);

  const load = () => {
    const params = {};
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    api.get(`/statutory-forms/${code}/entries`, { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [code, user]);

  if (!form) return <div className="py-16 text-center text-slate-400">Loading…</div>;

  const openAdd = () => { setEditing(null); setData({}); setShow(true); };
  const openEdit = (e) => { setEditing(e); setData(e.data || {}); setShow(true); };

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/statutory-forms/${code}/entries/${editing.id}`, { data });
        toast.success(lang === "hi" ? "प्रविष्टि अद्यतन" : "Entry updated");
      } else {
        await api.post(`/statutory-forms/${code}/entries`, { panchayat_id: user?.panchayat_id, data });
        toast.success(lang === "hi" ? "प्रविष्टि जोड़ी गई" : "Entry added");
      }
      setShow(false);
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (e) => {
    if (!window.confirm(lang === "hi" ? "प्रविष्टि हटाएँ?" : "Delete this entry?")) return;
    await api.delete(`/statutory-forms/${code}/entries/${e.id}`);
    toast.success(lang === "hi" ? "हटाई गई" : "Deleted");
    load();
  };

  const cols = form.fields.slice(0, 6);

  return (
    <div data-testid={`statutory-register-${code}`}>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#E87722]">
            {lang === "hi" ? "पंचायती राज अधिनियम — प्रपत्र" : "Panchayati Raj Act — FORM"} {form.code}
          </div>
          <h2 className="mt-1 font-heading text-xl font-bold text-slate-900">{lab(form.title)}</h2>
          {form.mapped && (
            <p className="mt-1 text-xs text-emerald-700 inline-flex items-center gap-1">
              <Database className="w-3 h-3" />
              {lang === "hi"
                ? "PDF निर्यात में संबंधित मॉड्यूल का डेटा स्वतः सम्मिलित होता है।"
                : "PDF export auto-includes records from this module."}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatutoryExport formCode={form.code} panchayatId={user?.panchayat_id} fy={localStorage.getItem("hsps_fy") || ""} />
          <button onClick={openAdd} data-testid="statutory-add-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#E87722" }}>
            <Plus className="w-4 h-4" /> {lang === "hi" ? "नई प्रविष्टि" : "New Entry"}
          </button>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="statutory-entries-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="text-left px-3 py-3">#</th>
              {cols.map((f) => <th key={f.key} className="text-left px-3 py-3">{lab(f.label)}</th>)}
              <th className="text-right px-3 py-3">{lang === "hi" ? "क्रियाएँ" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e, i) => (
              <tr key={e.id} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2.5 text-xs text-slate-400">{i + 1}</td>
                {cols.map((f) => (
                  <td key={f.key} className="px-3 py-2.5 text-xs max-w-[200px]">
                    <span className="line-clamp-2">{e.data?.[f.key] ?? ""}</span>
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right whitespace-nowrap space-x-1">
                  <button onClick={() => openEdit(e)} data-testid={`statutory-edit-${e.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-[#0A3161] text-[#0A3161]">
                    <Pencil className="w-3 h-3" /> {lang === "hi" ? "संपादित" : "Edit"}
                  </button>
                  <button onClick={() => remove(e)} data-testid={`statutory-del-${e.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-red-300 text-red-600">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={cols.length + 2} className="py-10 text-center text-slate-400">
                {lang === "hi" ? "कोई प्रविष्टि नहीं — नई प्रविष्टि जोड़ें" : "No entries yet — add a new entry"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="font-heading text-lg font-bold">
                {lang === "hi" ? "प्रपत्र" : "Form"} {form.code} — {editing ? (lang === "hi" ? "संपादन" : "Edit Entry") : (lang === "hi" ? "नई प्रविष्टि" : "New Entry")}
              </h2>
              <button onClick={() => setShow(false)} className="p-2 text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {form.fields.map((f) => (
                <div key={f.key} className={f.type === "a" ? "sm:col-span-2" : ""}>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">{lab(f.label)}</div>
                  {f.type === "a" ? (
                    <textarea rows={3} value={data[f.key] || ""} onChange={(ev) => setData({ ...data, [f.key]: ev.target.value })}
                      className="statutory-input" data-testid={`statutory-field-${f.key}`} />
                  ) : (
                    <input type={f.type === "n" ? "number" : f.type === "d" ? "date" : "text"} step="any"
                      value={data[f.key] || ""} onChange={(ev) => setData({ ...data, [f.key]: ev.target.value })}
                      className="statutory-input" data-testid={`statutory-field-${f.key}`} />
                  )}
                </div>
              ))}
              <div className="sm:col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300">
                  {lang === "hi" ? "रद्द करें" : "Cancel"}
                </button>
                <button type="submit" disabled={busy} data-testid="statutory-submit"
                  className="px-4 py-2 text-sm font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
                  {busy ? "…" : editing ? (lang === "hi" ? "अद्यतन करें" : "Update") : (lang === "hi" ? "जोड़ें" : "Add Entry")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <style>{`.statutory-input{width:100%;padding:0.55rem 0.75rem;border-radius:0.375rem;border:1px solid #CBD5E1;background:white;font-size:0.875rem;outline:none}.statutory-input:focus{border-color:#0A3161;box-shadow:0 0 0 3px rgba(10,49,97,0.08)}`}</style>
    </div>
  );
};
